/**
 * 発注計算に関するComposable
 */

import { computed, type ComputedRef } from 'vue';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { MonthlyDataCalculationService } from '@/coreDomainsModule/services/monthlyDataCalculationService';
import { useTableCalculation } from './useTableCalculation';
import type { IMonthlyBusinessData } from '../domains/entities/MonthlyData';
import type { IForecastAccuracy } from '../domains/entities/ForecastAccuracy';

/**
 * 数値として扱えるかどうかを判定する
 * @param value - 判定対象の値
 * @returns 数値ならtrue
 */
const isValidNumber = (value: number | undefined | null): value is number => typeof value === 'number' && !Number.isNaN(value);

/**
 * 発注計算機能を提供するComposable
 */
export const useOrderCalculation = () => {
  // テーブル計算Composableを使用
  const { getMonthlyData } = useTableCalculation();

  /**
   * 発注後の予想期末在庫を計算（リアクティブ対応）
   * @description 内示正答率が設定されている場合は補正内示（内示After）を使用して計算
   * @param monthlyDataCache - 月次データキャッシュ
   * @param orderEditData - 発注編集データ
   * @param forecastAccuracyData - 内示正答率データ
   * @returns リアクティブな計算関数
   */
  const calculateOrderModeExceptedEndInventory = computed(
    () =>
      (
        monthlyDataCache: Record<string, IMonthlyBusinessData>,
        orderEditData: Record<string, Record<number, any>>,
        business: IBusiness,
        month: number,
        forecastAccuracyData: Record<string, IForecastAccuracy> = {}
      ): string => {
        const monthlyData = getMonthlyData(monthlyDataCache, business.businessId, month);
        const baseValue = monthlyData.exceptedEndInventory;
        const originalAnnounce = monthlyData.announce || 0;

        if (!isValidNumber(baseValue)) {
          return '--';
        }

        // 発注数量の取得
        const changedOrderValue = orderEditData[business.businessId]?.[month]?.amount || 0;
        const changedCrossDate = orderEditData[business.businessId]?.[month]?.crossDate;
        const targetDate = DateService.addMonthsToCurrentDate(month);

        const hasOrderChange =
          changedCrossDate?.year === targetDate.year &&
          changedCrossDate?.month === targetDate.month &&
          !isNaN(changedOrderValue) &&
          changedOrderValue !== 0;

        // 内示正答率による補正の確認（当月以降）
        const settingAccuracy = month >= 0 ? forecastAccuracyData[business.businessId]?.settingAccuracy : undefined;
        const hasForecastAccuracyChange = settingAccuracy && settingAccuracy !== 100;

        // 補正内示の計算
        const adjustedAnnounce = hasForecastAccuracyChange
          ? Math.round(originalAnnounce * (settingAccuracy / 100) * 10) / 10
          : originalAnnounce;

        // 内示変更による予末在庫の差分を計算
        const forecastDifference = originalAnnounce - adjustedAnnounce;

        // 表示パターンの判定
        if (hasOrderChange && hasForecastAccuracyChange) {
          // 発注数量と内示正答率の両方が変更されている場合
          const newValue = baseValue + forecastDifference + changedOrderValue;
          return `${baseValue.roundToDecimal(2).addComma()} → ${newValue.roundToDecimal(2).addComma()}`;
        } else if (hasOrderChange) {
          // 発注数量のみ変更されている場合（従来通り）
          const newValue = baseValue + changedOrderValue;
          return `${baseValue.roundToDecimal(2).addComma()} → ${newValue.roundToDecimal(2).addComma()}`;
        } else if (hasForecastAccuracyChange) {
          // 内示正答率のみ変更されている場合
          const newValue = baseValue + forecastDifference;
          return `${baseValue.roundToDecimal(2).addComma()} → ${newValue.roundToDecimal(2).addComma()}`;
        } else {
          // 変更なしの場合
          return baseValue ? baseValue.roundToDecimal(2).addComma() : '--';
        }
      }
  );

  /**
   * 発注モードでの在庫回転日数を計算（リアクティブ対応）
   * @description 内示正答率が設定されている場合は補正内示（内示After）を使用して計算
   * @param monthlyDataCache - 月次データキャッシュ
   * @param orderEditData - 発注編集データ
   * @param forecastAccuracyData - 内示正答率データ
   * @returns リアクティブな計算関数
   */
  const calculateOrderModeTurnOver = computed(
    () =>
      (
        monthlyDataCache: Record<string, IMonthlyBusinessData>,
        orderEditData: Record<string, Record<number, any>>,
        business: IBusiness,
        month: number,
        forecastAccuracyData: Record<string, IForecastAccuracy> = {}
      ): string => {
        const monthlyData = getMonthlyData(monthlyDataCache, business.businessId, month);
        const beforeValue = monthlyData.turnOver;
        const originalAnnounce = monthlyData.announce || 0;

        if (!isValidNumber(beforeValue)) {
          return '--';
        }

        // 発注数量の取得
        const changedOrderValue = orderEditData[business.businessId]?.[month]?.amount || 0;
        const changedCrossDate = orderEditData[business.businessId]?.[month]?.crossDate;
        const targetDate = DateService.addMonthsToCurrentDate(month);

        const hasOrderChange =
          changedCrossDate?.year === targetDate.year &&
          changedCrossDate?.month === targetDate.month &&
          !isNaN(changedOrderValue) &&
          changedOrderValue !== 0;

        // 内示正答率による補正の確認（当月以降）
        const settingAccuracy = month >= 0 ? forecastAccuracyData[business.businessId]?.settingAccuracy : undefined;
        const hasForecastAccuracyChange = settingAccuracy && settingAccuracy !== 100;

        // 補正内示の計算
        const adjustedAnnounce = hasForecastAccuracyChange
          ? Math.round(originalAnnounce * (settingAccuracy / 100) * 10) / 10
          : originalAnnounce;

        // 内示変更による予末在庫の差分を計算
        const forecastDifference = originalAnnounce - adjustedAnnounce;

        if (hasOrderChange || hasForecastAccuracyChange) {
          // 予末在庫の再計算
          const baseExceptedEndInventory = monthlyData.exceptedEndInventory;
          if (!isValidNumber(baseExceptedEndInventory)) {
            return '--';
          }
          const adjustedExceptedEndInventory =
            baseExceptedEndInventory + (hasForecastAccuracyChange ? forecastDifference : 0) + (hasOrderChange ? changedOrderValue : 0);

          // 在庫回転率の再計算
          const afterTurnOver = MonthlyDataCalculationService.calcTurnOver(adjustedExceptedEndInventory, adjustedAnnounce);
          return `${beforeValue.roundToDecimal(2).addComma()} → ${afterTurnOver.roundToDecimal(2).addComma()}`;
        } else {
          return beforeValue.roundToDecimal(2).addComma();
        }
      }
  );

  /**
   * ヘッダーキーから月を取得
   * @param headerKey - ヘッダーキー（例: "transaction--1-"）
   * @returns 月数値（例: -1）
   */
  const getMonthFromHeaderKey = (headerKey: string): number => {
    const match = headerKey.match(/^transaction-(-?\d+)-/);
    return match ? Number(match[1]) : 0;
  };

  /**
   * 発注編集データの確保
   * @param orderEditData - 現在の発注編集データ
   * @param businessId - ビジネスID
   * @param month - 月番号
   */
  const ensureOrderEditData = (orderEditData: Record<string, Record<number, any>>, businessId: string, month: number): void => {
    if (!businessId) return;
    if (!orderEditData[businessId]) orderEditData[businessId] = {};
    if (!orderEditData[businessId][month]) {
      orderEditData[businessId][month] = {
        // IArrival型の必須プロパティ
        memoText: '',
        year: 0,
        month: 0,
        day: 0,
        // 既存プロパティ
        amount: 0,
        crossDate: undefined,
        arrivalId: '',
        orderNumber: '',
        leadTime: 0,
        isFixOrdered: false,
        fixOrderDate: undefined,
        status: undefined,
        // 必要なプロパティを追加
        displayId: '',
      };
    }
  };

  // 公開するAPIを返す
  return {
    calculateOrderModeExceptedEndInventory,
    calculateOrderModeTurnOver,
    getMonthFromHeaderKey,
    ensureOrderEditData,
  };
};
